/**
 * @fileoverview Toast notification context and provider.
 *
 * Provides global toast notification state management with support for
 * success, error, and info message types. Toasts auto-dismiss after 5 seconds
 * and can be manually dismissed.
 *
 * @example
 * ```tsx
 * // Wrap app with provider
 * <ToastProvider>
 *   <App />
 * </ToastProvider>
 *
 * // Use in components
 * const { addToast, removeToast } = useToast();
 * addToast({ type: 'success', message: 'Evidence uploaded!' });
 * ```
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  type ReactNode,
} from 'react';

/**
 * Toast type variants.
 */
export type ToastType = 'success' | 'error' | 'info';

/**
 * Individual toast notification data.
 */
export interface Toast {
  /** Unique identifier for the toast */
  id: string;
  /** Type of toast - determines color and icon */
  type: ToastType;
  /** Message to display */
  message: string;
  /** Optional title for the toast */
  title?: string;
  /** Auto-generated timestamp for tracking */
  createdAt: number;
}

/**
 * Parameters for creating a new toast.
 */
export interface AddToastParams {
  /** Type of toast */
  type: ToastType;
  /** Message to display */
  message: string;
  /** Optional title */
  title?: string;
  /** Duration in ms before auto-dismiss (default: 5000) */
  duration?: number;
}

/**
 * Toast context value interface.
 */
export interface ToastContextValue {
  /** Currently active toasts */
  toasts: Toast[];
  /**
   * Adds a new toast notification.
   *
   * @param params - Toast parameters
   * @returns The ID of the created toast
   */
  addToast: (params: AddToastParams) => string;
  /**
   * Removes a toast by ID.
   *
   * @param id - Toast ID to remove
   */
  removeToast: (id: string) => void;
  /**
   * Removes all toasts.
   */
  clearToasts: () => void;
}

/**
 * Toast context.
 * Use the useToast hook to access this context.
 */
const ToastContext = createContext<ToastContextValue | null>(null);

/**
 * Props for the ToastProvider component.
 */
interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Default auto-dismiss duration in milliseconds.
 */
const DEFAULT_DURATION = 5000;

/**
 * Maximum number of toasts to display at once.
 */
const MAX_TOASTS = 5;

/**
 * Generates a unique ID for toasts.
 */
function generateToastId(): string {
  return `toast-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Toast provider component.
 * Wraps the application to provide toast notification functionality.
 *
 * @param props - Provider props with children
 * @param props.children
 * @returns Provider component wrapping children
 */
export function ToastProvider({ children }: ToastProviderProps): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const addToast = useCallback(
    (params: AddToastParams): string => {
      const id = generateToastId();
      // Build toast object - only include title if provided (exactOptionalPropertyTypes compatibility)
      const toast: Toast = {
        id,
        type: params.type,
        message: params.message,
        createdAt: Date.now(),
        ...(params.title !== undefined && { title: params.title }),
      };

      setToasts((current) => {
        // Limit to MAX_TOASTS, removing oldest first
        const newToasts = [...current, toast];
        if (newToasts.length > MAX_TOASTS) {
          return newToasts.slice(newToasts.length - MAX_TOASTS);
        }
        return newToasts;
      });

      // Set up auto-dismiss timer
      const duration = params.duration ?? DEFAULT_DURATION;
      if (duration > 0) {
        setTimeout(() => {
          removeToast(id);
        }, duration);
      }

      return id;
    },
    [removeToast]
  );

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      toasts,
      addToast,
      removeToast,
      clearToasts,
    }),
    [toasts, addToast, removeToast, clearToasts]
  );

  return <ToastContext.Provider value={value}>{children}</ToastContext.Provider>;
}

/**
 * Hook to access the toast notification context.
 * Must be used within a ToastProvider.
 *
 * @returns The toast context value
 * @throws Error if used outside of ToastProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { addToast } = useToast();
 *
 *   const handleSave = async () => {
 *     try {
 *       await saveData();
 *       addToast({ type: 'success', message: 'Data saved successfully!' });
 *     } catch (error) {
 *       addToast({ type: 'error', message: 'Failed to save data.' });
 *     }
 *   };
 *
 *   return <button onClick={handleSave}>Save</button>;
 * }
 * ```
 */
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
